import React, { useCallback, useEffect, useState } from 'react';

import { webEncounterCapture, bindCaptureTransport } from '../../lib/encounterCapture';
import { decide } from '../../lib/captureEligibility';
import { encounterAppointmentId } from '../../lib/encounterRoom';

/**
 * M4 — governed capture control for an ACTIVE encounter consultation (Business Web).
 *
 * The third implementation of one product behaviour, matching Android's `EncounterCaptureBar` and
 * iOS's. The provider joins the consultation, the call surface takes over, and the control has to be
 * HERE — a control that lives only on appointment detail becomes unreachable the moment the call
 * starts, which is the defect this closes. Before it, the only way to record was to press Start
 * BEFORE connecting, inverting the governed order.
 *
 * ENCOUNTER-ONLY, STRUCTURALLY. It renders nothing unless the active call id resolves through
 * `encounterAppointmentId` (`enc_<appointment_id>`). A staff-to-staff call never carries that
 * identity, so staff calling cannot reach this control, cannot fetch readiness, and is unchanged.
 *
 * IT OPENS NO MICROPHONE. The producer is handed the CALL'S existing local stream, so it uses that
 * track or refuses. A second getUserMedia would be a second permission prompt and a second lifecycle
 * that could diverge from the call — muted in the call, live in the recording.
 *
 * IT SHARES ONE PRODUCER WITH THE APPOINTMENT-DETAIL CONTROL. `webEncounterCapture` is the whole
 * plane's single recorder, so a capture the provider started from Scheduling detail before joining
 * is ALREADY RUNNING when this bar mounts, and shows as running rather than offering to start a
 * second one. Stopping here stops that same capture.
 *
 * ONE MODE-AGNOSTIC IMPLEMENTATION. The room id carries no mode, so an audio consultation and a
 * video consultation use this same component with the same appointment and the same readiness.
 */
const EncounterCaptureBar = ({
  activeCallId,
  localStream,
  connected,
  businessId,
  fetchReadiness,        // (appointmentId, businessId) -> capture_readiness map | null
  sendSegment,           // (businessId, appointmentId, body, headers) -> Promise
}) => {
  const appointmentId = encounterAppointmentId(activeCallId);
  const [decision, setDecision] = useState(null);
  // WHICH appointment is recording, not merely whether this one is — see the same note in
  // AppointmentCaptureControl. A boolean would go unchanged when a different appointment started or
  // stopped, and React would skip the re-render.
  const [recordingAppointment, setRecordingAppointment] = useState(
    () => webEncounterCapture.runningAppointmentId(),
  );
  const running = recordingAppointment === appointmentId && recordingAppointment !== '';

  // The producer is the authority on whether a recording is in progress; this mirrors it. Mounting
  // mid-capture — the provider pressed Start on the appointment and then joined — must read as
  // running immediately, which a local flag initialised to false could not do.
  useEffect(() => {
    bindCaptureTransport(sendSegment);
    setRecordingAppointment(webEncounterCapture.runningAppointmentId());
    return webEncounterCapture.subscribe(() => {
      setRecordingAppointment(webEncounterCapture.runningAppointmentId());
    });
  }, [appointmentId, sendSegment]);

  // Readiness is fetched FRESH when the call connects — never inherited from the scheduling screen.
  // The provider may answer an encounter without ever opening Scheduling, so a cached decision would
  // be missing exactly when it is needed, or stale from a different appointment.
  useEffect(() => {
    let alive = true;
    // Readiness is still asked on CONNECTED — it is a question about a live consultation. The
    // control is visible before it resolves; it is simply not yet enabled.
    if (!appointmentId || !connected || !businessId) return undefined;
    (async () => {
      let readiness = null;
      try {
        readiness = await fetchReadiness(appointmentId, businessId);
      } catch (e) {
        readiness = null;     // unreadable readiness is NOT permission — decide() fails closed
      }
      if (!alive) return;
      const d = decide(readiness, 'audio');
      setDecision(d);
      // Capture is a CONTINUOUSLY granted permission: a withdrawal seen here must stop a capture
      // already running, not merely disable the button. This applies to a capture started on the
      // appointment-detail surface too — it is the same producer, and this is the first time the
      // call plane has re-read the patient's standing.
      if (webEncounterCapture.runningAppointmentId() === appointmentId) {
        await webEncounterCapture.onReadinessChanged(d);
      }
    })();
    return () => { alive = false; };
  }, [appointmentId, connected, businessId, fetchReadiness]);

  // The call ending must never leave the recorder running. This mirrors Android's resetCallState
  // cleanup — the one teardown every end path funnels through. Scoped to THIS appointment: the
  // producer is shared, and a staff call ending must not stop an encounter capture.
  useEffect(() => {
    if (connected || !appointmentId) return undefined;
    if (webEncounterCapture.runningAppointmentId() === appointmentId) {
      void webEncounterCapture.stop();
    }
    return undefined;
  }, [connected, appointmentId]);

  // No stop-on-unmount. The producer outlives this component now, and unmounting the call surface is
  // not the same event as the call ending — the disconnect effect above owns that.

  const onToggle = useCallback(() => {
    if (!appointmentId || !decision) return;
    (async () => {
      if (webEncounterCapture.isRunning()) { await webEncounterCapture.stop(); return; }
      // `true` is the operator's explicit request — the only thing that may start a capture.
      await webEncounterCapture.start({
        businessId, appointmentId, decision, operatorRequested: true, stream: localStream,
      });
      // No local running flag is set here: the producer publishes the transition, and taking its
      // word for it is what keeps this surface and the detail control from ever disagreeing.
    })();
  }, [appointmentId, decision, businessId, localStream]);

  // Not an encounter: render nothing. That is the staff-call path, and the ONLY suppression.
  //
  // Presentation follows IDENTITY, not media. Requiring `connected` (live remote audio) conflated
  // two concerns: whether this is a consultation — known the moment the enc_ room id exists — and
  // whether recording may begin. Anything that delayed live audio hid the control completely, so a
  // provider on a connected consultation had nowhere to start recording.
  if (!appointmentId) return null;

  // Visible while connecting; ENABLED only when the governed answer permits AND media is live.
  const enabled = !!(decision && decision.enabled === true) && connected;

  return (
    <div data-testid="encounter-capture-bar"
         style={{ padding: 12, background: '#263238', borderRadius: 8, marginTop: 12 }}>
      {enabled ? (
        <>
          <button
            type="button"
            data-testid="encounter-capture-toggle"
            onClick={onToggle}
            style={{ width: '100%', padding: 10, borderRadius: 6, border: 'none',
                     background: running ? '#B71C1C' : '#455A64', color: '#fff', fontWeight: 600,
                     cursor: 'pointer' }}
          >
            {running ? 'Stop recording' : 'Start recording'}
          </button>
          {running ? (
            <div style={{ color: '#B0BEC5', fontSize: 12, marginTop: 6 }}>
              Recording this consultation.
            </div>
          ) : null}
        </>
      ) : (
        <>
          {/* Truthfully unavailable, with the governed reason — never a dead button. */}
          <div style={{ color: '#B0BEC5', fontSize: 13 }}>Recording is currently unavailable.</div>
          {decision && decision.reason ? (
            <div data-testid="encounter-capture-reason"
                 style={{ color: '#90A4AE', fontSize: 12, marginTop: 4 }}>
              {decision.reason}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default EncounterCaptureBar;
